/**
 * The ASCII motifs.
 *
 * Text, not assets: they scale, recolour and print for free, and a customer card
 * that wants a different hue is a prop rather than a new file. Every motif is
 * drawn on a fixed grid and only lines up in `--font-mono` — `AsciiArt` sets
 * that, so nothing here should be rendered in a proportional face.
 *
 * `head` is how many leading lines take the caller's accent colour. The rest are
 * the constant leaf green, which is what makes three customer cards read as
 * three of the same plant rather than as three different drawings.
 */

import { colors } from "@/theme/colors"

export interface AsciiMotif {
  lines: readonly string[]
  head: number
}

/** The customer plant. The default: a card, a hero, a seat. */
const sprout: AsciiMotif = {
  lines: ["wwwww", "(___)", "\\~Y~/", " \\|// ", " ^^^^^"],
  head: 1,
}

/** The second plant, for a customer that would otherwise repeat its neighbour. */
const bloom: AsciiMotif = {
  lines: [" ''' ", "{{}}}", " ~Y~ ", "  |/ ", " \\|/ ", " \\|/ "],
  head: 2,
}

/** The third. Together these three cycle by position across a grid. */
const seedling: AsciiMotif = {
  lines: ["ooooo", "(___)", " ~Y~ ", " \\|/ ", "^^^^^"],
  head: 1,
}

/*
 * One row of four cards, one of them still blank. The deep dive's motif and the
 * job gate's — the two curtains that are about a hand already dealt.
 */
const hand: AsciiMotif = {
  lines: [
    "+-----+  +-----+  +-----+  +-----+",
    "|  =  |  |  =  |  |  =  |  |  *  |",
    "+-----+  +-----+  +-----+  +-----+",
  ],
  head: 0,
}

/*
 * A fanned stack, seen from the corner. The cards curtain's motif: that round
 * is about reading the whole deck, and a hand of four says the opposite.
 *
 * This name used to hold two rows of `hand`. Nothing draws that any more — the
 * curtains it was on now take `pains` and `job` — so it is redrawn rather than
 * kept as a spare.
 */
const deck: AsciiMotif = {
  lines: [
    "    .------.  ",
    "  .------. |  ",
    ".------. | |  ",
    "| 0000 | | |  ",
    "| 00   | | '  ",
    "| 000  | '    ",
    "'------'      ",
  ],
  head: 0,
}

/** Three sticky notes, two written and one still going. The Pains curtain. */
const pains: AsciiMotif = {
  lines: [
    ".----.  .----.",
    "|0000|  |00  |",
    "|00  |  |0000|",
    "'----'  '----'",
    " 0 0     0 0 0",
    "   .----.     ",
    "   |000 |     ",
    "   '----'     ",
  ],
  head: 0,
}

/*
 * The job frame with its three parts filled in — the shape of the sentence
 * before a word of it is written. The curtain that leads into drafting it.
 */
const job: AsciiMotif = {
  lines: [
    ".------------.",
    "| AS A       |",
    "| 0000000    |",
    "| I WANT     |",
    "| 000000000  |",
    "| SO THAT    |",
    "| 00000000   |",
    "'------------'",
  ],
  head: 0,
}

/** Four quadrants, filled unevenly. The Priorities board. */
const fit: AsciiMotif = {
  lines: [
    ".-----.-----.",
    "|0 00 |  0  |",
    "|00   |     |",
    "|-----+-----|",
    "| 000 |  0  |",
    "|     |     |",
    "'-----'-----'",
  ],
  head: 0,
}

/* The small glyphs scattered across the Join screen. Each is a few characters —
   they are punctuation on a large empty page, not illustrations. */

const spark: AsciiMotif = { lines: ["(*)", " >/"], head: 1 }
const grass: AsciiMotif = { lines: ["\\|/", "^^^"], head: 0 }
const stalk: AsciiMotif = { lines: ["www", "(_)", "\\|/", "^^^^"], head: 1 }
const crane: AsciiMotif = { lines: ["\\(| ,-", "  \\|/"], head: 1 }
const bud: AsciiMotif = { lines: ["(o)", "\\|/"], head: 1 }
const blade: AsciiMotif = { lines: [" \\|/", " ^^^"], head: 0 }
const speck: AsciiMotif = { lines: [".."], head: 0 }

export const ASCII = {
  sprout,
  bloom,
  seedling,
  hand,
  deck,
  pains,
  job,
  fit,
  spark,
  grass,
  stalk,
  crane,
  bud,
  blade,
  speck,
} as const

/**
 * The report mark: three stacked plates, each in its own hue.
 *
 * Drawn at a heavier weight than the motifs above because it is the only ASCII
 * in the app that is the SUBJECT of a screen rather than decoration on one — the
 * loading report has nothing else on its left half. It is never rendered still:
 * it exists to be assembled out of scattered glyphs by `AsciiAssembly`, and the
 * hue is what a glyph flies in on, not what it lands as.
 *
 * Every row is exactly `REPORT_MARK_COLUMNS` characters. Trailing spaces are
 * load-bearing — the grid is fixed-track, so a short row would centre wrong.
 */
export const REPORT_MARK_COLUMNS = 24

export const REPORT_MARK = [
  {
    hue: colors.motion.mark.warm,
    lines: [
      "      .:=######=:.      ",
      "    :#@#=:.  .:=#@#:    ",
      "   =@#:          :#@=   ",
      "   =@#:          :#@=   ",
      "    :#@#=:.  .:=#@#:    ",
      "      .:=######=:.      ",
    ],
  },
  {
    hue: colors.motion.mark.blue,
    lines: [
      "      =@#:      :#@=    ",
      "       :#@#:  :#@#:     ",
      "         :#@##@#:       ",
      "          :#@@#:        ",
      "         :#@##@#:       ",
      "       :#@#:  :#@#:     ",
      "      =@#:      :#@=    ",
    ],
  },
  {
    hue: colors.motion.mark.leaf,
    lines: [
      "     :#@@@@@@@@@@@#:    ",
      "    =@#:         :#@=   ",
      "    =@#:  :===:  :#@=   ",
      "    =@#:         :#@=   ",
      "     :#@@@@@@@@@@@#:    ",
      "        :=#####=:       ",
      "          :=#=:         ",
    ],
  },
] as const

export type AsciiName = keyof typeof ASCII

/**
 * The three plants, in the order a grid cycles them. Position, not identity:
 * the same customer keeps the same motif because its index in the list does not
 * move, and nothing is stored.
 */
export const PLANT_CYCLE: readonly AsciiName[] = ["sprout", "bloom", "seedling"]

/** The small glyphs, in the order the Join screen scatters them. */
export const SCATTER_CYCLE: readonly AsciiName[] = [
  "stalk",
  "grass",
  "spark",
  "bud",
  "crane",
  "blade",
  "seedling",
  "speck",
  "bloom",
  "grass",
  "spark",
  "stalk",
]
